import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonDropdown,
  Checkbox,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Modal,
  Popover,
  Select,
  SpaceBetween,
  Table,
  TextContent,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationCreateRequest,
  InvestigationFieldBreakdown,
  InvestigationPreview,
  InvestigationSample,
  InvestigationSuggestion,
  Stage,
  TrackStarsReportSummary,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useCreateInvestigation,
  useExtendPopulation,
  useInvestigationPreview,
} from "../../hooks/useInvestigations";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  InvestigationOverlapDialog,
  OverlapChoice,
} from "./InvestigationOverlapDialog";
import { AssignOwnerModal } from "./AssignOwnerModal";
import {
  builderDraftKey,
  clearSessionState,
  readSessionState,
  writeSessionState,
} from "./drawerSessionState";
import { DuplicateRuleAlert } from "./PreviewOverlaps";
import { PreviewVerdict } from "./PreviewVerdict";
import { SuggestedSplits } from "./SuggestedSplits";
import { TransactionLink } from "./TransactionLink";
import { DlqPkCell } from "./DlqEventModal";
import { LoadingLine } from "./LoadingLine";
import { RuleValueField, ruleValueOptions } from "./RuleValueField";
import {
  TrackStarsPatternHints,
  isPatternOwned,
  patternOwner,
  useMatchingPatterns,
} from "./BuilderHints";
import {
  BOOLEAN_RULE_OPTIONS,
  CreateOwnership,
  CreatedSummary,
  InvestigationRule,
  RULE_FIELDS,
  RowSeed,
  RuleOverrides,
  buildRule,
  createdSummary,
  describeSuggestionCoverage,
  excludedValues,
  fieldValues,
  formatSnapshotTimestamp,
  ownershipRequestFields,
  isExclusion,
  pinsUncategorized,
  predicateValueText,
  predicateValues,
  predicatesExcludingSeed,
  rawSeedValue,
  seedValue,
  splitTitle,
  titleForRule,
  trackStarsTitle,
  investigationPath,
  constraintAccepts,
} from "./investigationUtils";
import { shortPatternId } from "./trackstarsText";
import { dashboardPathForRule } from "../dashboard/ruleFilters";
import {
  BuilderCategorySource,
  categorySourceLinks,
  useErrorCategorySources,
} from "./CategorySource";

/** The investigation a "Split" starts from: its predicates are locked in and the result is its child. */
export interface SplitSource {
  investigationId: string;
  title?: string;
  rule: InvestigationRule;
  /**
   * A pattern of a TrackStars categorisation run on the parent: its derived predicates are
   * pre-ticked, its category names the child and the child records the pattern as its origin.
   */
  pattern?: {
    patternId: string;
    runId: string;
    category?: string | null;
    description?: string;
    rule: InvestigationRule;
  };
}

/**
 * A TrackStars pattern the builder starts from: the derived rule is pre-ticked (still editable),
 * the title, first comment and evidence links come from the report, and the case records the
 * pattern as its origin so the suggestion shows as owned afterwards.
 */
export interface SuggestionSource {
  suggestion: InvestigationSuggestion;
  reportId?: string;
  /**
   * The report the pattern came from. Its window is what `suggestion.totalMatches` was counted
   * over, so the coverage note can tell "the DLQ grew since the report" from "the rule is wider".
   */
  report?: TrackStarsReportSummary;
}

/** True when a templated error message satisfies every substring the pattern's when-clause requires. */
export const matchesMessageConstraints = (
  value: string | undefined,
  constraints: string[] | undefined
): boolean =>
  !!constraints?.length && constraintAccepts(value, { contains: constraints });

interface InvestigationPopulationBuilderProps {
  stage: Stage;
  seed: RowSeed;
  /** Optional: create the investigation as a recurrence of a resolved one. */
  recurrenceOfInvestigationId?: string;
  /**
   * Optional: split a narrower child off an existing investigation. The parent's predicates cannot
   * be unticked or changed (the child rule must stay a strict superset), at least one more field
   * must be pinned, and Create submits `mode=child` directly — the overlap dialog is skipped
   * because the parent overlap is the point.
   */
  splitFrom?: SplitSource;
  /** Optional: start from a TrackStars suggestion (see SuggestionSource). */
  fromSuggestion?: SuggestionSource;
  onCreated: (investigationId: string, summary: CreatedSummary) => void;
  onCancel: () => void;
  /** Open an existing investigation in place (the drawer); falls back to a link in a new tab. */
  onOpenExisting?: (investigationId: string) => void;
  /**
   * When set, the secondary action is a "back" step (e.g. to the DLQ item the rule was seeded
   * from) rather than a discard: it reads this label and keeps the draft so returning resumes it.
   */
  backLabel?: string;
}

/** Form state persisted per (stage, seed) so Back from a full page restores the draft. */
export interface BuilderDraft {
  selected: string[];
  /** Per-field value sets. (Drafts written before multi-value rules hold one string per field.) */
  overrides: Record<string, string[] | string>;
  /** A hand-typed title; null when the title follows the rule. */
  editedTitle: string | null;
  /** Per-field values the rule excludes (`not_in`), e.g. other cases' items. */
  exclusions?: Record<string, string[]>;
}

const draftOverrides = (
  draft: BuilderDraft | null | undefined
): RuleOverrides =>
  Object.fromEntries(
    Object.entries(draft?.overrides ?? {}).map(([field, value]) => [
      field,
      Array.isArray(value) ? value : [value],
    ])
  );

/**
 * The three preview counters and what each one means. Every number is taken from the latest
 * hourly DLQ snapshot.
 */
const PREVIEW_STATS: {
  label: string;
  info: string;
  value: (preview: InvestigationPreview) => number;
}[] = [
  {
    label: "Matching items",
    info: "Every DLQ item in the latest hourly snapshot that satisfies this rule, whoever owns it.",
    value: (p) => p.count ?? 0,
  },
  {
    label: "Unassigned",
    info: "Matching items no active investigation owns yet — the ones this investigation would take over.",
    value: (p) => p.unassignedCount ?? 0,
  },
  {
    label: "Owned by active investigations",
    info: "Matching items another open investigation already owns. They stay with that investigation; the overlap notice below names it.",
    value: (p) => p.assignedCount ?? 0,
  },
];

const StatInfo: React.FC<{ label: string; content: string }> = ({
  label,
  content,
}) => (
  <Popover
    triggerType="custom"
    dismissButton={false}
    size="medium"
    header={label}
    content={content}
  >
    <Button variant="inline-link" ariaLabel={`What “${label}” means`}>
      Info
    </Button>
  </Popover>
);

interface PinNotice {
  field: string;
  label: string;
  value: string;
  contains: string[];
}

interface OpenConstraint {
  field: string;
  label: string;
  contains: string[];
  matches: { value: string; count: number }[];
  count: number;
}

const Contains: React.FC<{ texts: string[] }> = ({ texts }) => (
  <>
    {texts.map((text, i) => (
      <React.Fragment key={text}>
        {i > 0 ? " and " : ""}
        <code>{text}</code>
      </React.Fragment>
    ))}
  </>
);

interface SuggestionCoverageAlertProps {
  patternId: string;
  count: number;
  target: number;
  report?: TrackStarsReportSummary;
  snapshotTimestamp?: string | null;
  activePins: PinNotice[];
  openConstraints: OpenConstraint[];
}

/**
 * What the investigation derived from a TrackStars pattern will take, against what TrackStars
 * reported. The user already chose the pattern, so this states the count and only raises its
 * voice (warning) when the gap is evidence the rule is wrong or a constraint is still to pin.
 * What an auto-pin did sits behind a disclosure so the ticked box is explained on demand.
 */
const SuggestionCoverageAlert: React.FC<SuggestionCoverageAlertProps> = ({
  patternId,
  count,
  target,
  report,
  snapshotTimestamp,
  activePins,
  openConstraints,
}) => {
  const coverage = describeSuggestionCoverage({
    count,
    target,
    report,
    snapshotTimestamp,
    // A constraint no value satisfies is not something the user can pin.
    hasOpenConstraints: openConstraints.some((c) => c.matches.length > 0),
  });
  return (
    <Alert
      type={openConstraints.length || coverage.attention ? "warning" : "info"}
      header={`TrackStars ${shortPatternId(patternId)}`}
      data-testid="suggestion-coverage"
    >
      <SpaceBetween size="xs">
        <div data-coverage-verdict={coverage.verdict}>{coverage.summary}</div>
        {openConstraints.map((c) => (
          <div key={c.field}>
            The pattern also needs the {c.label.toLowerCase()} to contain{" "}
            <Contains texts={c.contains} />.{" "}
            {c.matches.length === 0
              ? "No value in this population contains it — the rule stays wider than the pattern."
              : `${
                  c.matches.length
                } values (${c.count.toLocaleString()} items) do; pin one from the suggested splits below, or leave it open to take them all.`}
          </div>
        ))}
        {activePins.length > 0 && (
          <ExpandableSection
            headerText="What was pinned for you"
            variant="footer"
          >
            <SpaceBetween size="xs">
              {activePins.map((n) => (
                <div key={n.field} data-testid="auto-pin-notice">
                  {n.label} was pinned to <code>{n.value}</code>, the only value
                  in this population containing <Contains texts={n.contains} />.
                  Untick it to widen the rule again.
                </div>
              ))}
            </SpaceBetween>
          </ExpandableSection>
        )}
      </SpaceBetween>
    </Alert>
  );
};

interface PreviewSummaryProps {
  stage: Stage;
  preview?: InvestigationPreview;
  pending: boolean;
  /** What the wait is for when it is not the Athena query itself. */
  pendingMessage?: string;
  error?: string;
  ruleEmpty: boolean;
  /** Overlap with this investigation is expected (the split parent) and is not warned about. */
  expectedOverlapId?: string;
  currentUser?: string | null;
  onAddTo?: (investigationId: string) => void;
  adding?: boolean;
  addError?: string;
  /** Everything the decision box needs beyond the preview itself. */
  verdict: Omit<
    React.ComponentProps<typeof PreviewVerdict>,
    | "stage"
    | "preview"
    | "activeOverlaps"
    | "currentUser"
    | "onAddTo"
    | "adding"
    | "addError"
  >;
}

const renderSamplePk =
  (stage: Stage) =>
  // eslint-disable-next-line react/display-name
  (s: InvestigationSample) =>
    (
      <DlqPkCell
        stage={stage}
        dlqPk={s.dlqPk}
        transactionId={s.transactionId}
        orderId={s.orderId}
      />
    );

const renderSampleTransaction =
  (stage: Stage) =>
  // eslint-disable-next-line react/display-name
  (s: InvestigationSample) =>
    (
      <TransactionLink
        stage={stage}
        transactionId={s.transactionId}
        orderId={s.orderId}
      />
    );

interface SampleTableProps {
  stage: Stage;
  samples: InvestigationSample[];
  totalItems: number;
}

/**
 * Sample transactions: a uniform random pick of distinct transactions from the matching items
 * (one DLQ message each; the backend dedupes because one transaction fans out into several
 * messages). The builder lives in a narrow drawer, so the inline view is a single "Transaction"
 * column (the human-readable identity; the opaque PK only when no ID is known) and the full
 * table – PK, queue, error type, use case, owner – opens in a wide modal on demand.
 */
const SampleTable: React.FC<SampleTableProps> = ({
  stage,
  samples,
  totalItems,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const renderIdentity = (s: InvestigationSample) =>
    s.transactionId || s.orderId
      ? renderSampleTransaction(stage)(s)
      : renderSamplePk(stage)(s);
  return (
    <>
      <Table
        variant="embedded"
        header={
          <Header
            variant="h3"
            counter={`(${samples.length})`}
            description={`Random sample of distinct transactions from the ${totalItems.toLocaleString()} matching items. Not exhaustive.`}
            actions={
              <Button
                variant="normal"
                disabled={!samples.length}
                onClick={() => setShowDetails(true)}
              >
                View details…
              </Button>
            }
          >
            Sample transactions
          </Header>
        }
        items={samples}
        columnDefinitions={[
          { id: "txn", header: "Transaction", cell: renderIdentity },
        ]}
        empty={<Box textAlign="center">No matching items</Box>}
      />
      {showDetails && (
        <Modal
          visible
          size="max"
          onDismiss={() => setShowDetails(false)}
          header={
            <Header
              variant="h2"
              counter={`(${samples.length})`}
              description={`Random sample of distinct transactions from the ${totalItems.toLocaleString()} matching items.`}
            >
              Sample transactions
            </Header>
          }
          footer={
            <Box float="right">
              <Button variant="primary" onClick={() => setShowDetails(false)}>
                Close
              </Button>
            </Box>
          }
        >
          <Table
            variant="embedded"
            items={samples}
            columnDefinitions={[
              { id: "pk", header: "DLQ PK", cell: renderSamplePk(stage) },
              {
                id: "txn",
                header: "Transaction",
                cell: renderSampleTransaction(stage),
              },
              { id: "queue", header: "Queue", cell: (s) => s.eventsourcekey },
              { id: "type", header: "Error type", cell: (s) => s.errorType },
              { id: "usecase", header: "Use case", cell: (s) => s.usecase },
              {
                id: "owner",
                header: "Owned by",
                cell: (s) => s.primaryInvestigationId || "—",
              },
            ]}
            empty={<Box textAlign="center">No matching items</Box>}
          />
        </Modal>
      )}
    </>
  );
};

const PreviewSummary: React.FC<PreviewSummaryProps> = ({
  stage,
  preview,
  pending,
  pendingMessage,
  error,
  ruleEmpty,
  expectedOverlapId,
  currentUser,
  onAddTo,
  adding,
  addError,
  verdict,
}) => {
  const activeOverlaps = (preview?.activeOverlaps || []).filter(
    (o) => !expectedOverlapId || o.investigationId !== expectedOverlapId
  );
  if (ruleEmpty)
    return <Alert type="info">Select at least one field with a value.</Alert>;
  if (error)
    return (
      <Alert type="error" header="Preview failed">
        {error}
      </Alert>
    );
  if (pending || !preview) {
    return (
      <LoadingLine align="left">
        {pendingMessage ?? "Querying the latest DLQ snapshot…"}
      </LoadingLine>
    );
  }
  if (preview.status === "FAILED")
    return (
      <Alert type="error" header="Preview failed">
        {preview.error}
      </Alert>
    );
  if (preview.truncated)
    return (
      <Alert
        type="warning"
        header={`Population too large (${preview.count ?? 0} items)`}
      >
        {preview.error ||
          `The maximum population for an exact manifest is ${
            preview.maxPopulation ?? 50000
          } items. Add more fields to narrow the rule.`}
      </Alert>
    );
  return (
    <SpaceBetween size="s">
      <KeyValuePairs
        columns={3}
        items={PREVIEW_STATS.map((stat) => ({
          label: stat.label,
          info: <StatInfo label={stat.label} content={stat.info} />,
          value: stat.value(preview),
        }))}
      />
      <TextContent>
        <small>
          Snapshot {formatSnapshotTimestamp(preview.snapshotTimestamp)}{" "}
          (hourly).
        </small>
      </TextContent>
      <PreviewVerdict
        stage={stage}
        preview={preview}
        activeOverlaps={activeOverlaps}
        currentUser={currentUser}
        onAddTo={onAddTo}
        adding={adding}
        addError={addError}
        {...verdict}
      />
      <SampleTable
        stage={stage}
        samples={preview.samples || []}
        totalItems={preview.count ?? 0}
      />
    </SpaceBetween>
  );
};

/**
 * Replaces the old "“Uncategorised” is temporary" warning. Picking `uncategorized` selects today's
 * uncategorised items; on creation Lusca stores the pattern minus the categories it already had
 * elsewhere, so the case keeps tracking its items after the categoriser names them (R14.1).
 */
export const UncategorizedCaption: React.FC<{
  /** error_category is the only field: nothing else defines the pattern, so it is kept as-is. */
  standalone: boolean;
  /** Unused since the caption no longer names them; kept so callers need not change. */
  // eslint-disable-next-line react/no-unused-prop-types
  categorizedElsewhere?: InvestigationPreview["categorizedElsewhere"];
}> = ({ standalone }) => {
  if (standalone) {
    return (
      <Box
        fontSize="body-s"
        color="text-status-warning"
        margin={{ top: "xxs" }}
        data-testid="uncategorized-caption"
      >
        Add another field (error type, message or queue): on its own,
        “uncategorized” stops matching once these items are categorised.
      </Box>
    );
  }
  // Items already categorised elsewhere are not in the preview at all, so naming their categories
  // here only confuses; `categorizedElsewhere` stays on the props for callers.
  return (
    <Box
      fontSize="body-s"
      color="text-body-secondary"
      margin={{ top: "xxs" }}
      data-testid="uncategorized-caption"
    >
      Items stay in this case after they get a category.
    </Box>
  );
};

export const InvestigationPopulationBuilder: React.FC<
  InvestigationPopulationBuilderProps
> = ({
  stage,
  seed,
  recurrenceOfInvestigationId,
  splitFrom,
  fromSuggestion,
  onCreated,
  onCancel,
  onOpenExisting,
  backLabel,
}) => {
  // A pattern adopted from the "TrackStars already identified this" hint: same starter handling
  // as fromSuggestion (description, origin, links) but the rule stays the user's own — or is
  // narrowed to the pattern, in which case `before` lets Undo put the rule back.
  const [adopted, setAdopted] = useState<
    | (SuggestionSource & {
        before?: { selected: Set<string>; overrides: RuleOverrides };
      })
    | null
  >(null);
  const suggestion = fromSuggestion?.suggestion ?? adopted?.suggestion;
  const suggestionReportId = fromSuggestion?.reportId ?? adopted?.reportId;
  // Parent predicates that must stay in the rule unchanged for the child to be a strict subset,
  // with the value set each binds (an `in` parent field stays an `in`).
  const lockedValues = useMemo(
    () =>
      Object.fromEntries(
        (splitFrom?.rule || [])
          .filter((p) => !isExclusion(p))
          .map((p) => [p.field as string, predicateValues(p)])
      ) as RuleOverrides,
    [splitFrom]
  );
  // A parent stored from an "uncategorised" rule excludes the categories its pattern already had
  // (`not_in`); a child carries the exclusion through unless it pins that field itself.
  const inheritedExclusions = useMemo(
    () => (splitFrom?.rule || []).filter(isExclusion),
    [splitFrom]
  );
  const lockedFields = useMemo(
    () => new Set(Object.keys(lockedValues)),
    [lockedValues]
  );
  const draftKey = builderDraftKey(
    stage,
    seed,
    recurrenceOfInvestigationId,
    (splitFrom &&
      `${splitFrom.investigationId}${
        splitFrom.pattern ? `#${splitFrom.pattern.patternId}` : ""
      }`) ??
      (suggestion ? `trackstars=${suggestion.patternId}` : undefined)
  );
  const [draft] = useState(() => readSessionState<BuilderDraft>(draftKey));
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        draft?.selected ??
          (splitFrom
            ? [
                ...Array.from(lockedFields),
                ...(splitFrom.pattern?.rule || [])
                  .map((p) => p.field as string)
                  .filter((f) => !lockedFields.has(f)),
              ]
            : suggestion
            ? (suggestion.derivedRule || []).map((p) => p.field as string)
            : RULE_FIELDS.filter(
                (f) => f.defaultSelected && seedValue(seed, f.field)
              ).map((f) => f.field))
      )
  );
  // Fields whose last value was just removed: unticked (so the rule ignores them) but kept in
  // place with an empty input, so swapping a value doesn't bury the field under "More fields".
  const [emptiedFields, setEmptiedFields] = useState<Set<string>>(
    () => new Set()
  );
  const [overrides, setOverrides] = useState<RuleOverrides>(() =>
    draftOverrides(draft)
  );
  /** Values a field must NOT take (`not_in`), added by "Exclude items owned by other cases". */
  const [exclusions, setExclusions] = useState<RuleOverrides>(
    () => draft?.exclusions ?? {}
  );
  /** A hand-typed title; null means the title follows the rule (see `title` below). */
  const [editedTitle, setEditedTitle] = useState<string | null>(
    () => draft?.editedTitle ?? null
  );
  /** Ownership choice for the create in flight; the split button sets it, the overlap dialog reads it. */
  const [ownership, setOwnership] = useState<CreateOwnership>("me");
  const [assignVisible, setAssignVisible] = useState(false);
  const [showOverlap, setShowOverlap] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  /** The active case a create was refused for (DUPLICATE_RULE); cleared when the rule changes. */
  const [duplicateOf, setDuplicateOf] = useState<Investigation | null>(null);
  const previewState = useInvestigationPreview(stage);
  const create = useCreateInvestigation(stage);

  // Keep the draft in session storage so navigating away and back does not start from scratch.
  useEffect(() => {
    writeSessionState(draftKey, {
      selected: Array.from(selected),
      overrides,
      editedTitle,
      exclusions,
    } as BuilderDraft);
  }, [draftKey, selected, overrides, editedTitle, exclusions]);

  const categorySources = useErrorCategorySources();
  const rule: InvestigationRule = useMemo(() => {
    let built: InvestigationRule;
    if (!splitFrom) built = buildRule(seed, selected, overrides);
    else {
      // Locked fields are always in and always carry the parent's values, whatever the draft says.
      const effectiveSelected = new Set(selected);
      const effectiveOverrides: RuleOverrides = {
        ...overrides,
        ...lockedValues,
      };
      lockedFields.forEach((field) => effectiveSelected.add(field));
      built = buildRule(seed, effectiveSelected, effectiveOverrides);
    }
    // Exclusions (the parent's, and the builder's own) apply to fields the rule does not pin.
    const pinned = new Set(built.map((p) => p.field as string));
    const excluded = new Map<string, Set<string>>();
    [
      ...inheritedExclusions.map(
        (p) => [p.field as string, excludedValues(p)] as const
      ),
      ...Object.entries(exclusions),
    ].forEach(([field, values]) => {
      if (pinned.has(field) || !values.length) return;
      const set = excluded.get(field) ?? new Set<string>();
      values.forEach((v) => set.add(v));
      excluded.set(field, set);
    });
    return [
      ...built,
      ...Array.from(excluded.entries()).map(
        ([field, values]) =>
          ({
            field,
            operator: "not_in",
            values: Array.from(values).sort(),
          } as unknown as InvestigationRule[number])
      ),
    ];
  }, [
    seed,
    selected,
    overrides,
    exclusions,
    splitFrom,
    lockedFields,
    lockedValues,
    inheritedExclusions,
  ]);
  const ruleSignature = JSON.stringify(rule);
  // A child must be strictly narrower: at least one predicate beyond the parent's.
  const narrowed =
    !splitFrom || rule.length > lockedFields.size + inheritedExclusions.length;

  // The title tracks the rule until someone types their own, so ticking a field off (or narrowing
  // on one) is reflected in the name; a hand-typed title is kept and can be reset to the suggestion.
  const suggestedTitle = useMemo(
    () =>
      splitFrom?.pattern?.category
        ? trackStarsTitle(splitFrom.pattern.category, rule)
        : splitFrom
        ? splitTitle(
            splitFrom.title || splitFrom.investigationId,
            rule,
            lockedFields
          )
        : suggestion?.suggestedCategory
        ? // TrackStars' proposed error_category is the shortest human name for the pattern.
          trackStarsTitle(suggestion.suggestedCategory, rule)
        : titleForRule(rule),
    [splitFrom, rule, lockedFields, suggestion?.suggestedCategory]
  );
  const title = editedTitle ?? suggestedTitle;

  // Re-run the preview whenever the effective rule changes (debounced). `previewedFor` is the
  // rule the shown preview answers; while it lags the current rule the preview is stale and is
  // presented as pending rather than flashing numbers for a rule the user no longer has. (Starts
  // as the mount-time rule: there is no preview to be stale until the first run.)
  const [previewedFor, setPreviewedFor] = useState<string | null>(
    ruleSignature
  );
  useEffect(() => {
    setDuplicateOf(null);
    if (!rule.length) {
      previewState.reset();
      setPreviewedFor(null);
      return undefined;
    }
    const handle = setTimeout(() => {
      setPreviewedFor(ruleSignature);
      previewState.run(rule).catch(console.error);
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialised rule on purpose
  }, [ruleSignature]);

  const { preview } = previewState;
  const stale = previewedFor !== ruleSignature;
  const uncategorizedPinned = pinsUncategorized(rule);
  const completed = preview?.status === "COMPLETED" && !stale;
  const currentUser = useCurrentUser();
  const extend = useExtendPopulation(stage);
  // "Add to <case>": the owner of a case that already covers most of the draft tops it up with
  // this preview's items instead of creating a near-duplicate (the case's rule is unchanged).
  const addToExisting = (investigationId: string) => {
    if (!completed || !preview?.previewId) return;
    extend
      .mutateAsync({ investigationId, previewId: preview.previewId })
      .then(() => {
        clearSessionState(draftKey);
        if (onOpenExisting) onOpenExisting(investigationId);
        else window.location.assign(investigationPath(investigationId, stage));
      })
      .catch(() => undefined); // shown via extend.error
  };

  // The values each field can be pinned to, from the latest completed preview's breakdown. A
  // field that is pinned is no longer in the breakdown (its value is fixed), so the distribution
  // seen just before it was pinned is kept: that is the list the user chose from, and it is what
  // they need again to change their mind. Unpinning refreshes it from the next preview.
  const [knownBreakdowns, setKnownBreakdowns] = useState<
    Record<string, InvestigationFieldBreakdown>
  >({});
  useEffect(() => {
    if (!completed || !preview?.fieldBreakdown?.length) return;
    setKnownBreakdowns((current) => {
      const next = { ...current };
      preview.fieldBreakdown?.forEach((b) => {
        next[b.field] = b;
      });
      return next;
    });
  }, [completed, preview]);

  const seedValues = useMemo(
    () =>
      Object.fromEntries(
        RULE_FIELDS.map((f) => [f.field, rawSeedValue(seed, f.field)])
      ) as Record<string, string | undefined>,
    [seed]
  );

  // A TrackStars when-clause can say "field contains X" (a message fragment, a queue-name stem);
  // an eq rule needs the exact value. The preview's breakdown tells which value(s) carry X: one
  // match is pinned for the user, several are pointed out, none means the rule stays wider.
  const constraints = useMemo(
    () =>
      // Only a "contains" constraint names values to pin; "does not contain" / "is not" terms
      // narrow which of them qualify.
      (suggestion?.constraints ?? []).filter(
        (c) => c.field && c.contains?.some(Boolean)
      ),
    [suggestion?.constraints]
  );
  const openConstraints = useMemo(
    () =>
      constraints
        .filter((c) => !rule.some((p) => p.field === c.field))
        .map((c) => {
          const breakdown = completed
            ? preview?.fieldBreakdown?.find((b) => b.field === c.field)
            : undefined;
          const matches = (breakdown?.values ?? []).filter((v) =>
            constraintAccepts(v.value, c)
          );
          return {
            field: c.field,
            contains: c.contains,
            matches,
            count: matches.reduce((n, v) => n + v.count, 0),
            label:
              RULE_FIELDS.find((f) => f.field === c.field)?.label ?? c.field,
          };
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialised rule on purpose
    [constraints, ruleSignature, completed, preview?.fieldBreakdown]
  );
  const [autoPinnedFor, setAutoPinnedFor] = useState<string | null>(null);
  /** What the last auto-pin did, so the user is told rather than finding a box ticked. */
  const [pinNotice, setPinNotice] = useState<
    { field: string; label: string; value: string; contains: string[] }[]
  >([]);
  // A single-match pin is about to change the rule: the preview on screen is for the rule before
  // the pin, so it is held back (as pending) instead of flashing and being replaced.
  const pinImminent =
    autoPinnedFor !== ruleSignature &&
    openConstraints.some((c) => c.matches.length === 1);
  // True from the pin until the preview of the pinned rule lands: names the wait for the user.
  const [pinResolving, setPinResolving] = useState(false);
  useEffect(() => {
    if (completed) setPinResolving(false);
  }, [completed]);
  useEffect(() => {
    // Exactly one value matches a constraint: pin it once (the user may still untick it).
    const single = openConstraints.filter((c) => c.matches.length === 1);
    if (!single.length || autoPinnedFor === ruleSignature) return;
    setAutoPinnedFor(ruleSignature);
    setPinResolving(true);
    setPinNotice(
      single.map((c) => ({
        field: c.field,
        label: c.label,
        value: c.matches[0].value,
        contains: c.contains,
      }))
    );
    setSelected((current) => {
      const next = new Set(current);
      single.forEach((c) => next.add(c.field));
      return next;
    });
    setOverrides((current) => ({
      ...current,
      ...Object.fromEntries(single.map((c) => [c.field, [c.matches[0].value]])),
    }));
  }, [openConstraints, autoPinnedFor, ruleSignature]);
  // The notice is only true while the pinned value is still in the rule.
  const activePins = pinNotice.filter((n) =>
    rule.some(
      (p) =>
        p.field === n.field &&
        predicateValues(p).length === 1 &&
        predicateValues(p)[0] === n.value
    )
  );
  /** The preview shown answers the current rule and nothing is about to change it. */
  const settled = completed && !previewState.pending && !pinImminent;

  /**
   * "Exclude items owned by other cases": a `not_in` carve-out becomes the field's exclusion list;
   * an `in` / `eq` one (the other case itself excludes values) pins the field instead.
   */
  const applyOwnedExclusion = (predicates: InvestigationRule) => {
    const excludeNext: RuleOverrides = { ...exclusions };
    const pinNext: RuleOverrides = {};
    predicates.forEach((p) => {
      const field = p.field as string;
      if (isExclusion(p)) excludeNext[field] = excludedValues(p);
      else {
        pinNext[field] = predicateValues(p);
        delete excludeNext[field];
      }
    });
    setExclusions(excludeNext);
    if (Object.keys(pinNext).length) {
      setSelected((current) => {
        const next = new Set(current);
        Object.keys(pinNext).forEach((f) => next.add(f));
        return next;
      });
      setOverrides((current) => ({ ...current, ...pinNext }));
    }
  };
  const removeExclusion = (field: string) => {
    const next = { ...exclusions };
    delete next[field];
    setExclusions(next);
  };
  // Own exclusions still in effect (a field the rule now pins ignores its exclusion).
  const shownExclusions = rule.filter(
    (p) => isExclusion(p) && (exclusions[p.field as string]?.length ?? 0) > 0
  );

  /** "Narrow this population": pin a field to one of the population's values. */
  const applySplit = (field: string, value: string) => {
    setSelected((current) => new Set(current).add(field));
    setOverrides((current) => ({ ...current, [field]: [value] }));
  };

  // Predicates whose value differs from the DLQ item the rule was seeded from: the population no
  // longer contains that item. Only meaningful when the builder actually started from an item.
  const excludingPredicates = useMemo(
    () =>
      seed.dlqPk
        ? predicatesExcludingSeed(rule, seed).filter(
            // Leaving other cases' items out on purpose may drop the seed item: that is the point.
            (p) => !(isExclusion(p) && exclusions[p.field as string]?.length)
          )
        : [],
    [rule, seed, exclusions]
  );
  // Dismissed for this seed item: the user meant to leave it out.
  const [seedAlertDismissedFor, setSeedAlertDismissedFor] = useState<
    string | null
  >(null);
  const includeMyItem = () => {
    setOverrides((current) => {
      const next = { ...current };
      excludingPredicates.forEach((p) => {
        const value = rawSeedValue(seed, p.field);
        if (value) next[p.field] = [value];
        else delete next[p.field];
      });
      return next;
    });
    setSelected((current) => {
      const next = new Set(current);
      // A field the item has no value for cannot be pinned at all: untick it.
      excludingPredicates
        .filter((p) => !rawSeedValue(seed, p.field))
        .forEach((p) => next.delete(p.field));
      return next;
    });
  };
  // Item-level or rule-level overlap: both need an explicit decision so duplicate rules are never
  // created by accident (e.g. after a retry).
  const hasActiveOverlap =
    completed &&
    ((preview?.assignedCount ?? 0) > 0 ||
      (preview?.activeOverlaps?.length ?? 0) > 0);

  const submit = async (
    choice: OverlapChoice,
    ownershipNow: CreateOwnership = ownership
  ) => {
    if (!preview?.previewId) return;
    setCreateError(null);
    // TrackStars' evidence first, then the CR that added each pinned error category.
    const links = [
      ...(suggestion?.starter?.links ?? []).map((link) => ({
        ...link,
        source: "trackstars" as const,
      })),
      ...categorySourceLinks([rule], categorySources.data),
    ].slice(0, 8);
    const request: InvestigationCreateRequest = {
      previewId: preview.previewId,
      title: title.trim() || "Untitled investigation",
      ...ownershipRequestFields(ownershipNow),
      mode:
        choice.kind === "remainder"
          ? "remainder"
          : choice.kind === "child"
          ? "child"
          : "full",
      parentInvestigationId:
        choice.kind === "child" ? choice.parentInvestigationId : undefined,
      recurrenceOfInvestigationId,
      titleSource: editedTitle !== null ? "user" : "system",
      ...(splitFrom?.pattern && {
        description: splitFrom.pattern.description,
        origin: {
          source: "trackstars" as const,
          patternId: splitFrom.pattern.patternId,
          reportId: splitFrom.pattern.runId,
        },
      }),
      ...(suggestion && {
        // The title follows the rule like every other case; TrackStars' diagnosis is the description.
        description: suggestion.starter?.description || suggestion.description,
        origin: {
          source: "trackstars" as const,
          patternId: suggestion.patternId,
          reportId: suggestionReportId,
        },
        // No comment in the creator's name: the analysis is TrackStars', shown on its own tab,
        // and its evidence is attributed to TrackStars, not the creator.
      }),
      ...(links.length > 0 && { links }),
    } as InvestigationCreateRequest;
    try {
      const result = await create.mutateAsync(request);
      setShowOverlap(false);
      setAssignVisible(false);
      if (result.investigation?.id) {
        clearSessionState(draftKey);
        onCreated(result.investigation.id, createdSummary(result));
      }
    } catch (err) {
      const apiError = err as InvestigationApiError;
      if (apiError.code === "DUPLICATE_RULE" && apiError.current) {
        // Nothing was created: point at the case that already exists instead of showing an error.
        setShowOverlap(false);
        setAssignVisible(false);
        setDuplicateOf(apiError.current);
        return;
      }
      setCreateError(
        apiError.code === "PREVIEW_STALE"
          ? `${apiError.message}. Re-running the preview.`
          : apiError.message
      );
      if (apiError.code === "PREVIEW_STALE")
        previewState.run(rule, { fresh: true }).catch(console.error);
    }
  };

  const onCreateClick = (ownershipNow: CreateOwnership) => {
    setOwnership(ownershipNow);
    if (splitFrom) {
      submit(
        { kind: "child", parentInvestigationId: splitFrom.investigationId },
        ownershipNow
      ).catch(console.error);
    } else if (hasActiveOverlap) {
      setShowOverlap(true);
    } else {
      submit({ kind: "full" }, ownershipNow).catch(console.error);
    }
  };

  const createLabel = splitFrom
    ? "Split out and claim"
    : hasActiveOverlap
    ? "Review overlaps and create"
    : "Create and claim";
  const createUnownedLabel = splitFrom
    ? "Split out without owner"
    : "Create without owner";
  const createAssignLabel = splitFrom
    ? "Split out and assign to…"
    : "Create and assign to…";
  const createDisabled =
    !completed ||
    (preview?.count ?? 0) === 0 ||
    !!preview?.truncated ||
    !narrowed ||
    !!duplicateOf;
  const createDisabledReason = !narrowed
    ? "Pin at least one more field so the new case is narrower than the one it is split from."
    : duplicateOf
    ? "An active investigation already has exactly this rule."
    : undefined;

  // TrackStars patterns the draft touches: shown in the preview's collapsed overlaps, and an
  // unclaimed one that explains most of a wider draft becomes the recommended action.
  const { matches: patternMatches, reportId: patternReportId } =
    useMatchingPatterns(
      stage,
      rule,
      fromSuggestion?.suggestion.patternId,
      splitFrom?.investigationId,
      completed ? preview?.fieldBreakdown : undefined
    );
  const adoptPattern = (picked: InvestigationSuggestion, reportId?: string) => {
    setAdopted({
      suggestion: picked,
      reportId,
      before: { selected, overrides },
    });
    // The pattern names the population; "uncategorised" is transient (R14.2).
    setSelected((current) => {
      const next = new Set(current);
      if (
        fieldValues(seed, "error_category", overrides).includes("uncategorized")
      )
        next.delete("error_category");
      return next;
    });
  };
  const narrowToPattern = (
    picked: InvestigationSuggestion,
    reportId?: string
  ) => {
    setAdopted({
      suggestion: picked,
      reportId,
      before: { selected, overrides },
    });
    const derived = (picked.derivedRule ?? []) as InvestigationRule;
    setSelected((current) => {
      const next = new Set(current);
      derived.forEach((p) => next.add(p.field as string));
      if (
        !derived.some((p) => p.field === "error_category") &&
        fieldValues(seed, "error_category", overrides).includes("uncategorized")
      )
        next.delete("error_category");
      return next;
    });
    setOverrides((current) => ({
      ...current,
      ...Object.fromEntries(
        derived.map((p) => [p.field as string, predicateValues(p)])
      ),
    }));
  };
  const undoAdopt = () => {
    if (adopted?.before) {
      setSelected(adopted.before.selected);
      setOverrides(adopted.before.overrides);
    }
    setAdopted(null);
  };
  const freePatterns = patternMatches.filter(
    (m) =>
      !isPatternOwned(m.suggestion) &&
      m.suggestion.patternId !== adopted?.suggestion.patternId
  );
  const patternByInvestigation: Record<string, string> = {};
  patternMatches
    .filter((m) => isPatternOwned(m.suggestion))
    .forEach((m) => {
      const id = patternOwner(m.suggestion)?.investigationId;
      if (id && m.suggestion.patternId)
        patternByInvestigation[id] = m.suggestion.patternId;
    });

  const isFieldShown = (field: string) =>
    lockedFields.has(field) || selected.has(field) || emptiedFields.has(field);
  // Ticked fields stay on top; unticked ones collapse under "More fields" so the rule reads at a glance.
  const tickedFields = RULE_FIELDS.filter((d) => isFieldShown(d.field));
  const untickedFields = RULE_FIELDS.filter((d) => !isFieldShown(d.field));
  const withoutEmptied = (field: string) => {
    const next = new Set(emptiedFields);
    next.delete(field);
    return next;
  };
  const renderRuleRow = (definition: (typeof RULE_FIELDS)[number]) => {
    const locked = lockedFields.has(definition.field);
    const values = locked
      ? lockedValues[definition.field]
      : fieldValues(seed, definition.field, overrides);
    const value = values[0] ?? "";
    const checked = locked || selected.has(definition.field);
    const emptied = !checked && emptiedFields.has(definition.field);
    return (
      <div
        key={definition.field}
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(180px, 220px) minmax(0, 1fr)",
          gap: "8px",
          alignItems: "start",
        }}
      >
        <Checkbox
          checked={checked}
          disabled={locked}
          onChange={({ detail }) => {
            const next = new Set(selected);
            if (detail.checked) next.add(definition.field);
            else next.delete(definition.field);
            setSelected(next);
            // Any explicit tick/untick ends the "emptied" limbo: unticking sends the field to
            // "More fields", ticking restores the row's own value.
            if (emptied) {
              setEmptiedFields(withoutEmptied(definition.field));
              const rest = { ...overrides };
              delete rest[definition.field];
              setOverrides(rest);
            }
          }}
        >
          {definition.label}
          {locked ? " (from original)" : ""}
        </Checkbox>
        <div>
          {/* An unticked field is simply absent from the rule: render no input rather
                than a greyed-out box, so the ticked fields stand out. */}
          {(checked || emptied) &&
            (definition.boolean ? (
              <Select
                ariaLabel={`${definition.label} value`}
                disabled={locked}
                placeholder="true / false"
                options={BOOLEAN_RULE_OPTIONS}
                selectedOption={
                  BOOLEAN_RULE_OPTIONS.find(
                    (o) => o.value === value.toLowerCase()
                  ) ?? null
                }
                onChange={({ detail }) =>
                  setOverrides({
                    ...overrides,
                    [definition.field]: [detail.selectedOption.value ?? ""],
                  })
                }
              />
            ) : (
              <RuleValueField
                label={definition.label}
                values={values}
                disabled={locked}
                placeholder="Pick or type a value"
                options={ruleValueOptions(knownBreakdowns[definition.field])}
                distinctValues={
                  knownBreakdowns[definition.field]?.distinctValues
                }
                onChange={(next) => {
                  if (next.every((v) => v === "")) {
                    // Removing the last value drops the field from the rule; untick it so
                    // the checkbox never claims a field the rule ignores, but keep the row
                    // (with an empty input) where it is so a replacement can be typed.
                    setOverrides({ ...overrides, [definition.field]: [] });
                    const nextSelected = new Set(selected);
                    nextSelected.delete(definition.field);
                    setSelected(nextSelected);
                    setEmptiedFields(
                      new Set(emptiedFields).add(definition.field)
                    );
                    return;
                  }
                  setOverrides({
                    ...overrides,
                    [definition.field]: next,
                  });
                  if (emptied) {
                    // A replacement value re-pins the field.
                    setSelected(new Set(selected).add(definition.field));
                    setEmptiedFields(withoutEmptied(definition.field));
                  }
                }}
              />
            ))}
          {definition.field === "error_category" && checked && (
            <BuilderCategorySource categories={values} />
          )}
          {definition.field === "error_category" &&
            checked &&
            uncategorizedPinned && (
              <UncategorizedCaption
                standalone={rule.length < 2}
                categorizedElsewhere={
                  completed ? preview?.categorizedElsewhere : undefined
                }
              />
            )}
        </div>
      </div>
    );
  };

  return (
    <SpaceBetween size="m">
      <Container
        header={
          <Header
            variant="h3"
            description={
              splitFrom
                ? "The original case's fields are locked in. Tick at least one more field to narrow the population; the new case takes over those items and the original keeps the rest."
                : suggestion
                ? `Derived from TrackStars ${shortPatternId(
                    suggestion.patternId
                  )}. Every field is editable; the preview tells you how much of the pattern the rule covers.`
                : "Stage is implicit. Tick the fields that define the population; values come from the selected row. Each dropdown lists the values the previewed population contains; pick several to match any of them."
            }
          >
            Population rule
          </Header>
        }
      >
        <SpaceBetween size="xs">
          {tickedFields.map(renderRuleRow)}
          {shownExclusions.map((p) => {
            const label =
              RULE_FIELDS.find((f) => f.field === p.field)?.label ?? p.field;
            const values = excludedValues(p);
            return (
              <div
                key={`not-${p.field}`}
                data-testid={`exclusion-${p.field}`}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(180px, 220px) minmax(0, 1fr) auto",
                  gap: "8px",
                  alignItems: "start",
                }}
              >
                <Box fontWeight="bold">{label}</Box>
                <Box>
                  <Box variant="span" color="text-body-secondary">
                    is none of{" "}
                  </Box>
                  <span title={values.join("\n")}>{values.join(" · ")}</span>
                  <Box fontSize="body-s" color="text-body-secondary">
                    Leaves out the items other cases own.
                  </Box>
                </Box>
                <Button
                  variant="inline-link"
                  ariaLabel={`Remove the ${label} exclusion`}
                  onClick={() => removeExclusion(p.field as string)}
                >
                  Remove
                </Button>
              </div>
            );
          })}
          {untickedFields.length > 0 && (
            <ExpandableSection
              headerText={`More fields (${untickedFields.length})`}
              variant="footer"
            >
              <SpaceBetween size="xs">
                {untickedFields.map(renderRuleRow)}
              </SpaceBetween>
            </ExpandableSection>
          )}
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h3"
            actions={
              // The whole population, not just the samples: the DLQ dashboard with this rule as filters.
              <Button
                href={dashboardPathForRule(stage, rule)}
                target="_blank"
                iconName="external"
                iconAlign="right"
                disabled={!rule.length}
                data-testid="preview-open-dashboard"
              >
                Open in dashboard
              </Button>
            }
          >
            Preview
          </Header>
        }
      >
        <SpaceBetween size="s">
          {excludingPredicates.length > 0 &&
            seedAlertDismissedFor !== seed.dlqPk && (
              <Alert
                type="warning"
                dismissible
                dismissAriaLabel="Dismiss"
                onDismiss={() => setSeedAlertDismissedFor(seed.dlqPk ?? null)}
                header="The DLQ item you started from is not in this population"
                action={
                  <Button onClick={includeMyItem}>Include my item</Button>
                }
              >
                {excludingPredicates.map((p) => {
                  const label =
                    RULE_FIELDS.find((f) => f.field === p.field)?.label ??
                    p.field;
                  const mine = rawSeedValue(seed, p.field);
                  return (
                    <div key={p.field}>
                      {label} = <strong>{predicateValueText(p, " or ")}</strong>{" "}
                      excludes it (your item has{" "}
                      {mine ? <strong>{mine}</strong> : "no value"}).
                    </div>
                  );
                })}
                <div>
                  Switch the value back, or untick the field, to include it.
                </div>
              </Alert>
            )}
          {suggestion && settled && (
            <SuggestionCoverageAlert
              patternId={suggestion.patternId}
              count={preview?.count ?? 0}
              target={suggestion.totalMatches || 0}
              report={fromSuggestion?.report}
              snapshotTimestamp={preview?.snapshotTimestamp}
              activePins={activePins}
              openConstraints={openConstraints}
            />
          )}
          <PreviewSummary
            stage={stage}
            preview={preview}
            pending={previewState.pending || stale || pinImminent}
            pendingMessage={
              pinImminent || pinResolving
                ? "Pinning the exact value the pattern's substring resolves to, then re-querying…"
                : undefined
            }
            error={previewState.error?.message}
            ruleEmpty={!rule.length}
            expectedOverlapId={splitFrom?.investigationId}
            currentUser={currentUser}
            onAddTo={splitFrom ? undefined : addToExisting}
            adding={extend.isLoading}
            addError={extend.error?.message}
            verdict={{
              rule,
              freePatterns,
              patternByInvestigation,
              splitMode: !!splitFrom,
              createDisabled,
              createDisabledReason,
              creating: create.isLoading,
              onCreate: (choice) => {
                setOwnership("me");
                submit(
                  splitFrom
                    ? {
                        kind: "child",
                        parentInvestigationId: splitFrom.investigationId,
                      }
                    : choice,
                  "me"
                ).catch(console.error);
              },
              onNarrow: (m) => narrowToPattern(m.suggestion, patternReportId),
              onAdopt: (m) => adoptPattern(m.suggestion, patternReportId),
              onExcludeOwned: applyOwnedExclusion,
              trackStars: rule.length > 0 && (
                <TrackStarsPatternHints
                  embedded
                  stage={stage}
                  rule={rule}
                  currentPatternId={fromSuggestion?.suggestion.patternId}
                  adoptedPatternId={adopted?.suggestion.patternId}
                  excludeInvestigationId={splitFrom?.investigationId}
                  breakdown={completed ? preview?.fieldBreakdown : undefined}
                  onUseStarter={adoptPattern}
                  onNarrowToPattern={narrowToPattern}
                  onUndoAdopt={undoAdopt}
                />
              ),
            }}
          />
          {splitFrom && !narrowed && rule.length > 0 && (
            <Alert type="info" header="Not narrower than the original yet">
              This is still the original case&apos;s exact population. Pin at
              least one more field (or apply a suggested split below) to define
              the subset the new case should take over.
            </Alert>
          )}
          {settled && (
            <SuggestedSplits
              fieldBreakdown={preview?.fieldBreakdown}
              totalCount={preview?.count}
              maxPopulation={preview?.maxPopulation}
              seedValues={seedValues}
              defaultExpanded={!!preview?.truncated}
              onApply={applySplit}
            />
          )}
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h3">Create</Header>}>
        <SpaceBetween size="s">
          <FormField
            label="Title"
            description={
              editedTitle === null
                ? suggestion?.suggestedCategory
                  ? `Named after TrackStars ${shortPatternId(
                      suggestion.patternId
                    )}'s category; follows the rule until you edit it.`
                  : "Follows the population rule until you edit it."
                : undefined
            }
            secondaryControl={
              editedTitle !== null && editedTitle !== suggestedTitle ? (
                <Button
                  variant="inline-link"
                  onClick={() => setEditedTitle(null)}
                >
                  Use suggested title
                </Button>
              ) : undefined
            }
          >
            <Input
              value={title}
              onChange={({ detail }) => setEditedTitle(detail.value)}
            />
          </FormField>
          {recurrenceOfInvestigationId && (
            <Badge color="blue">
              Linked recurrence of {recurrenceOfInvestigationId}
            </Badge>
          )}
          {splitFrom && (
            <Badge color="blue">
              Split from {splitFrom.title || splitFrom.investigationId}
            </Badge>
          )}
          {suggestion && (
            <Badge color="blue">
              From TrackStars {shortPatternId(suggestion.patternId)}
            </Badge>
          )}
          {duplicateOf && (
            <DuplicateRuleAlert
              stage={stage}
              existing={duplicateOf}
              onOpen={onOpenExisting}
            />
          )}
          {createError && <Alert type="error">{createError}</Alert>}
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              iconName={backLabel ? "angle-left" : undefined}
              onClick={() => {
                if (!backLabel) clearSessionState(draftKey);
                onCancel();
              }}
            >
              {backLabel ?? "Cancel"}
            </Button>
            <ButtonDropdown
              variant="primary"
              ariaLabel="More ways to create"
              disabled={createDisabled}
              mainAction={{
                text: createLabel,
                disabled: createDisabled,
                disabledReason: createDisabledReason,
                loading: create.isLoading,
                onClick: () => onCreateClick("me"),
              }}
              items={[
                {
                  id: "create-assign",
                  text: createAssignLabel,
                  secondaryText: "Pick a Lusca Search user to own it",
                },
                {
                  id: "create-unowned",
                  text: createUnownedLabel,
                  secondaryText: hasActiveOverlap
                    ? "Review overlaps first; anyone can claim the result later"
                    : "Leave it on the Investigations page for anyone to claim",
                },
              ]}
              onItemClick={({ detail }) => {
                if (detail.id === "create-unowned") onCreateClick("nobody");
                if (detail.id === "create-assign") setAssignVisible(true);
              }}
            />
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {assignVisible && (
        <AssignOwnerModal
          stage={stage}
          visible
          title={title.trim() || "Untitled investigation"}
          confirmLabel={
            hasActiveOverlap && !splitFrom
              ? "Assign and review overlaps"
              : splitFrom
              ? "Split out and assign"
              : "Create and assign"
          }
          loading={create.isLoading}
          errorText={createError}
          onDismiss={() => setAssignVisible(false)}
          onConfirm={(owner) => {
            // With overlaps the dialog decides the mode; the picked owner carries through.
            if (hasActiveOverlap && !splitFrom) setAssignVisible(false);
            onCreateClick({ owner });
          }}
        />
      )}

      {preview && showOverlap && (
        <InvestigationOverlapDialog
          visible
          stage={stage}
          preview={preview}
          onDismiss={() => setShowOverlap(false)}
          onChoose={(choice) => {
            submit(choice).catch(console.error);
          }}
        />
      )}
    </SpaceBetween>
  );
};

export default InvestigationPopulationBuilder;
